import { useEffect, useRef, useState } from 'react';
import { ImageOff, Upload, X } from 'lucide-react';
import { cn } from './cn';
import { Button } from './Button';
import {
  UPLOAD_FORMAT_GUIDE,
  dimensionGuideFor,
  validateImageFile,
  type ImageUploadQueue,
  type MediaCategory,
  type UploadSlot,
} from '@/lib/upload';

export interface ImageUploadFieldProps {
  queue: ImageUploadQueue;
  name: string;
  category: MediaCategory;
  value: string | null;
  onChange: (url: string | null) => void;
  label?: string | undefined;
  disabled?: boolean | undefined;
  /**
   * 해당 surface 의 권장 규격 안내(예: "권장 1200×600px · 2:1"). 근거가 있는 화면만 전달한다.
   * 포맷/용량 가이드(`UPLOAD_FORMAT_GUIDE`)는 항상 노출되므로 여기엔 픽셀/비율만 담는다.
   */
  guide?: string | undefined;
  /** 미리보기 칸 비율(기본 정사각). 표시 규격이 16:9 인 화면(팝업스토어 썸네일 등)은 '16:9'. */
  previewRatio?: '1:1' | '16:9' | undefined;
}

/**
 * 단일 이미지 업로드 필드. 드래그&드롭 + 클릭 선택(accept image/*), 로컬 미리보기.
 * 선택 즉시 pending 등록 + 미리보기만, 실제 업로드는 queue.flush() 시점("저장 시 업로드").
 */
export const ImageUploadField = ({
  queue,
  name,
  category,
  value,
  onChange,
  label,
  disabled,
  guide,
  previewRatio = '1:1',
}: ImageUploadFieldProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isDragging, setDragging] = useState(false);

  const slotsRef = useRef<ReadonlyArray<UploadSlot>>([]);
  slotsRef.current = pendingFile
    ? [{ kind: 'pending', file: pendingFile }]
    : value
      ? [{ kind: 'existing', url: value }]
      : [];

  const { register, unregister } = queue;
  // 슬롯(파일 선택/해제)이 바뀔 때마다 재등록하여 큐의 pending 집계(hasPending)를 갱신한다.
  // register 는 동일 name 을 덮어쓰며 refreshPending 을 호출하므로 idempotent 하다.
  // (마운트 1회만 등록하면 파일 선택 후에도 hasPending 이 false 로 남아 "저장 시 업로드"
  //  모델에서 thumbnailUrl 이 비어 있는 신규 폼의 제출 게이트가 영구히 막힌다.)
  useEffect(() => {
    register(name, { category, multiple: false, getSlots: () => slotsRef.current });
  }, [register, name, category, pendingFile, value]);
  useEffect(() => () => unregister(name), [unregister, name]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const acceptFile = (file: File) => {
    const issue = validateImageFile(file);
    if (issue) {
      setLocalError(issue);
      return;
    }
    setLocalError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const clear = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPendingFile(null);
    setPreviewUrl(null);
    setLocalError(null);
    onChange(null);
  };

  const onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) acceptFile(file);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) acceptFile(file);
  };

  const onDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    if (!disabled) setDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(false);
  };

  const display = previewUrl ?? value;

  // ID57 — 명시 guide 가 없으면 surface(category) 권장 px/비율을 자동 적용(SSoT). 포맷/용량은 항상 표기.
  const effectiveGuide = guide ?? dimensionGuideFor(category);

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <span className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
          {label}
        </span>
      ) : null}
      <div className="flex items-stretch gap-3">
        <div
          className={cn(
            'flex h-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-md',
            previewRatio === '16:9' ? 'w-[171px]' : 'w-24',
          )}
          data-testid={`${name}-preview`}
          style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
        >
          {display ? (
            <img src={display} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <ImageOff size={20} style={{ color: 'var(--content-soft)' }} aria-hidden="true" />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <label
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className={cn(
              'flex flex-1 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-4 text-center text-[13px] transition-colors',
              disabled && 'cursor-not-allowed opacity-50',
            )}
            style={{
              borderColor: isDragging ? 'var(--content-strong)' : 'var(--divider-strong)',
              background: isDragging ? 'var(--surface-1)' : 'var(--surface-0)',
              color: 'var(--content-muted)',
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={onSelect}
              disabled={disabled}
              className="sr-only"
            />
            <Upload size={16} aria-hidden="true" />
            <span style={{ color: 'var(--content-strong)' }}>
              이미지를 끌어다 놓거나 클릭하여 선택
            </span>
            <span className="text-[11px]">
              {effectiveGuide ? `${effectiveGuide} · ${UPLOAD_FORMAT_GUIDE}` : UPLOAD_FORMAT_GUIDE}
            </span>
          </label>
          {display ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clear}
              disabled={disabled}
              className="self-start"
            >
              <X size={14} style={{ color: 'var(--signal-danger)' }} />
              삭제
            </Button>
          ) : null}
        </div>
      </div>
      {localError ? (
        <p role="alert" className="text-[11px]" style={{ color: 'var(--signal-danger)' }}>
          {localError}
        </p>
      ) : null}
    </div>
  );
};
